import { useEffect, useMemo } from "react";
import { useSelector } from "react-redux";
import { usePageFilter } from "../../../hooks/usePageFilters";
import {
  selectUser,
  selectAvailableClients,
} from "../../../redux/slices/userSlice";
import Client from "../../../redux/models/client";
import K from "../../../utilities/constants";
import { isPermissionPresent } from "../../../utilities/generalUtility";

const pagePrefix = "dashboard";

export const useDashboardFilters = () => {
  const userSlice = useSelector(selectUser);
  const filteredClients = useSelector(selectAvailableClients);

  const isSuperAdmin = isPermissionPresent(
    [K.Permissions.SuperAdmin],
    userSlice.roles,
  );

  const hasTeamViewPermission = isPermissionPresent(
    K.Permissions.ViewDashboardTeamProgress,
    userSlice.roles || [],
  );

  const isTeamView = isSuperAdmin || hasTeamViewPermission;
  const userSliceId = userSlice.details?.id;
  const userSliceName = userSlice.details?.name || "";

  const getInitialClient = () => {
    if (!isTeamView && filteredClients?.length === 1 && filteredClients[0]) {
      return {
        value: filteredClients[0].id,
        label: filteredClients[0].name,
      };
    }
    return null;
  };

  const getInitialRecruiter = (clientValue) => {
    if (!isTeamView && userSliceId && clientValue) {
      return {
        value: userSliceId,
        label: userSliceName,
      };
    }
    return null;
  };

  const [client, setClient] = usePageFilter(
    `${pagePrefix}_client`,
    getInitialClient(),
  );

  const [recruiter, setRecruiter] = usePageFilter(
    `${pagePrefix}_recruiter`,
    getInitialRecruiter(client?.value),
  );

  const clientId = client?.value;

  useEffect(() => {
    if (
      clientId &&
      filteredClients &&
      !filteredClients.some((c) => c.id === clientId)
    ) {
      setClient(null);
      setRecruiter(null);
    }
  }, [filteredClients, clientId, setClient, setRecruiter]);

  const fetchRecruiters = useMemo(() => {
    if (!clientId) {
      return () => Promise.resolve({ data: [] });
    }
    return () => Client.v2.getRecruitersByClientId(clientId, {}, false, false);
  }, [clientId]);

  const handleClientChange = (newClientValue) => {
    if (isTeamView) {
      setRecruiter(null);
    } else if (newClientValue && userSliceId) {
      setRecruiter({
        value: userSliceId,
        label: userSliceName,
      });
    } else {
      setRecruiter(null);
    }
    setClient(newClientValue);
  };

  const handleRecruiterChange = (selectedValue) => {
    setRecruiter(selectedValue);
  };

  const showEmptyState = !clientId || !recruiter?.value;

  return {
    client,
    recruiter,
    clientId,
    isTeamView,
    fetchRecruiters,
    handleClientChange,
    handleRecruiterChange,
    showEmptyState,
  };
};
